const debugEnabled = () => {
  try {
    return !!(window.store && window.store.debug);
  } catch (_) {
    return false;
  }
};

const dlog = (...args) => {
  if (!debugEnabled()) return;
  try {
    console.debug('[deferred-video]', ...args);
  } catch (_) {}
};

// Global delay: wait for page load + 2 seconds before any video loads
// This lets images load first without complex tracking
const VIDEO_DELAY = 2000;
let videoLoadAllowed = false;
let pendingVideos = [];

const initVideoLoading = () => {
  if (videoLoadAllowed) return;

  const allowLoading = () => {
    setTimeout(() => {
      videoLoadAllowed = true;
      dlog('video loading allowed after', VIDEO_DELAY, 'ms delay');
      // Trigger any pending videos
      pendingVideos.forEach(fn => { try { fn(); } catch (_) {} });
      pendingVideos = [];
    }, VIDEO_DELAY);
  };

  if (document.readyState === 'complete') {
    allowLoading();
  } else {
    window.addEventListener('load', allowLoading, { once: true });
  }
};

// Initialize on first video
initVideoLoading();

class DeferredVideo extends HTMLElement {
  constructor() {
    super();
    this._loaded = false;
    this._video = null;
    this._observer = null;
    this._intersectionHandler = this._onIntersection.bind(this);
  }

  connectedCallback() {
    try {
      this._template = this.querySelector('template');
      if (!this._template) {
        dlog('missing template');
        return;
      }

      const loadMode = (this.getAttribute('data-load') || 'visible').toLowerCase();

      // immediate: load right now (bypasses delay)
      if (loadMode === 'immediate') {
        this._doLoad();
        return;
      }

      // visible (default): load when scrolled into viewport + global delay
      if (!('IntersectionObserver' in window)) {
        dlog('no IntersectionObserver, loading with delay');
        this._scheduleLoad();
        return;
      }

      const threshold = this._getThreshold();
      const rootMargin = this.getAttribute('data-root-margin') || '0px 0px 200px 0px';

      this._observer = new IntersectionObserver(this._intersectionHandler, {
        root: null,
        rootMargin,
        threshold,
      });
      this._observer.observe(this);
    } catch (error) {
      dlog('connectedCallback error', error);
    }
  }

  _scheduleLoad() {
    if (this._loaded) return;

    if (videoLoadAllowed) {
      this._doLoad();
    } else {
      // Wait for global delay
      pendingVideos.push(() => {
        if (this.isConnected && !this._loaded) {
          this._doLoad();
        }
      });
    }
  }

  disconnectedCallback() {
    try {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._shouldPauseOnDisconnect()) {
        this._pause();
      }
    } catch (error) {
      dlog('disconnectedCallback error', error);
    }
  }

  // Public API
  load() {
    try {
      this._load();
      return this._video;
    } catch (_) {
      return null;
    }
  }

  play() {
    try {
      if (!this._loaded) {
        this._doLoad();
        return;
      }
      this._requestPlay();
    } catch (_) {}
  }

  pause() {
    try {
      this._pause();
    } catch (_) {}
  }

  get video() {
    return this._video || null;
  }

  _getThreshold() {
    const attr = parseFloat(this.getAttribute('data-threshold'));
    if (!Number.isNaN(attr) && attr >= 0 && attr <= 1) return attr;
    return 0;
  }

  _doLoad() {
    this._load();
    if (this._shouldAutoplay()) {
      this._ensureAutoplay();
    }
  }

  _load() {
    if (this._loaded) return;
    const tpl = this._template;
    if (!tpl || !tpl.content) return;
    try {
      const fragment = tpl.content.cloneNode(true);
      // iOS/Safari: set critical autoplay flags BEFORE inserting into the DOM
      try {
        const fv = fragment.querySelector && fragment.querySelector('video');
        if (fv) {
          fv.setAttribute('muted', '');
          fv.setAttribute('playsinline', '');
          fv.setAttribute('webkit-playsinline', '');
          fv.setAttribute('autoplay', '');
          const preloadAttr = this.getAttribute('data-preload') || 'auto';
          fv.setAttribute('preload', preloadAttr);
          try { fv.defaultMuted = true; } catch (_) {}
          try { fv.muted = true; } catch (_) {}
          try { fv.playsInline = true; fv.webkitPlaysinline = true; } catch (_) {}
          try { fv.volume = 0; } catch (_) {}
        }
      } catch (_) {}
      this.appendChild(fragment);
      this._video = this.querySelector && this.querySelector('video');
      this._loaded = true;
      this.setAttribute('data-loaded', 'true');
      tpl.remove();
      this._syncVideoAttributes();
      try {
        if (this._video) {
          const onCanPlay = () => { try { this._ensureAutoplay(); } catch (_) {} };
          this._video.addEventListener('loadeddata', onCanPlay, { once: true });
          this._video.addEventListener('canplay', onCanPlay, { once: true });
          this._ensureAutoplay();
        }
      } catch (_) {}
      this.dispatchEvent(new CustomEvent('deferred-video:loaded', {
        bubbles: true,
        detail: { video: this._video },
      }));
      dlog('loaded');
    } catch (error) {
      dlog('load error', error);
    }
  }

  _syncVideoAttributes() {
    if (!this._video) return;
    try {
      const preload = this.getAttribute('data-preload');
      if (preload) this._video.setAttribute('preload', preload);
      const muted = this.getAttribute('data-muted');
      if (muted === 'false') {
        this._video.removeAttribute('muted');
        try { this._video.muted = false; } catch (_) {}
      } else {
        this._video.setAttribute('muted', '');
        try { this._video.muted = true; } catch (_) {}
      }
      const playsinline = this.getAttribute('data-playsinline');
      if (playsinline === 'false') {
        this._video.removeAttribute('playsinline');
      } else {
        this._video.setAttribute('playsinline', '');
        this._video.setAttribute('webkit-playsinline', '');
      }
      const loop = this.getAttribute('data-loop');
      if (loop === 'true' || loop === '') this._video.setAttribute('loop', '');
      else if (loop === 'false') this._video.removeAttribute('loop');
      const controls = this.getAttribute('data-controls');
      if (controls === 'true') this._video.setAttribute('controls', '');
      else if (controls === 'false') this._video.removeAttribute('controls');
      const poster = this.getAttribute('data-poster');
      if (poster) this._video.setAttribute('poster', poster);
      if (this._shouldAutoplay()) this._video.setAttribute('autoplay', '');
      else this._video.removeAttribute('autoplay');
      try { this._video.playsInline = true; } catch (_) {}
      try { this._video.webkitPlaysinline = true; } catch (_) {}
      try { this._video.volume = 0; } catch (_) {}
    } catch (error) {
      dlog('sync error', error);
    }
  }

  _ensureAutoplay() {
    if (!this._video) return;
    try {
      this._video.muted = true;
      this._video.playsInline = true;
      this._video.webkitPlaysinline = true;
      if (!this._video.hasAttribute('muted')) this._video.setAttribute('muted', '');
      if (!this._video.hasAttribute('playsinline')) this._video.setAttribute('playsinline', '');
      if (!this._video.hasAttribute('webkit-playsinline')) this._video.setAttribute('webkit-playsinline', '');
      if (!this._video.hasAttribute('autoplay')) this._video.setAttribute('autoplay', '');
      try { this._video.volume = 0; } catch (_) {}
      const attemptPlay = () => {
        try {
          const p = this._video.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {}).catch((err) => {
              const msg = (err && (err.name || err.message)) || '';
              if ((/NotAllowed/i).test(String(msg))) {
                this._autoplayAttempts = (this._autoplayAttempts || 0) + 1;
                if (this._autoplayAttempts <= 5) {
                  setTimeout(attemptPlay, 150);
                } else {
                  dlog('autoplay blocked after retries');
                }
              }
            });
          }
        } catch (error) {
          dlog('play error', error);
        }
      };
      if (this._video.readyState >= 2) attemptPlay();
      else {
        try { this._video.load(); } catch (_) {}
      }
    } catch (error) {
      dlog('ensureAutoplay error', error);
    }
  }

  _onIntersection(entries) {
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      if (!entry || entry.target !== this) continue;
      if (entry.isIntersecting && entry.intersectionRatio >= this._getThreshold()) {
        dlog('visible');
        this._scheduleLoad();
      } else if (this._loaded && this._pauseWhenHidden()) {
        dlog('hidden -> pause');
        this._pause();
      }
    }
  }

  _shouldAutoplay() {
    const attr = this.getAttribute('data-autoplay');
    if (attr === 'false') return false;
    return attr === '' || attr === 'true' || attr === null;
  }

  _pauseWhenHidden() {
    const attr = this.getAttribute('data-pause-when-hidden');
    return attr !== 'false';
  }

  _shouldPauseOnDisconnect() {
    const attr = this.getAttribute('data-pause-on-disconnect');
    return attr !== 'false';
  }

  _requestPlay() {
    if (!this._video) return;
    try {
      const promise = this._video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch((err) => dlog('play blocked', err));
      }
    } catch (error) {
      dlog('play error', error);
    }
  }

  _pause() {
    if (!this._video) return;
    try {
      this._video.pause();
    } catch (error) {
      dlog('pause error', error);
    }
  }
}

try {
  if (!customElements.get('deferred-video')) {
    customElements.define('deferred-video', DeferredVideo);
  }
} catch (error) {
  dlog('define error', error);
}
